export interface JourneyMapProviderOption {
  value: string;
  label: string;
  defaultContentRefType?: string;
  contentRefTypes: string[];
}

export interface JourneyMapContentRefTypeOption {
  value: string;
  label: string;
  endpoint: string;
  displayFields: string[];
}

export const JOURNEY_MAP_CONTENT_REF_TYPES: Record<string, JourneyMapContentRefTypeOption> = {
  ai_snackable_course: {
    value: 'ai_snackable_course',
    label: 'Cyber Academy Course',
    endpoint: 'db/ai-snackable-course/',
    displayFields: ['title', 'name', 'id'],
  },
  // A course that exists in Moodle but has no ai_snackable_course row behind it. Authors do
  // not pick this type directly — the builder assigns it when the chosen course is not
  // covered by a snackable row, so those courses stay selectable instead of being filtered
  // out of the list. The id is a *Moodle* course id, not a toolbox_db primary key, which is
  // why the type exists at all: reusing `ai_snackable_course` would send the id to
  // `db/ai-snackable-course/{id}/` and resolve an unrelated row that happens to share it.
  moodle_course: {
    value: 'moodle_course',
    label: 'Moodle Course',
    endpoint: '',
    displayFields: ['fullname', 'shortname', 'title', 'name', 'id'],
  },
  resource: {
    value: 'resource',
    label: 'Resource',
    endpoint: 'db/resource/',
    displayFields: ['name', 'title', 'id'],
  },
  document_reference: {
    value: 'document_reference',
    label: 'Document Reference',
    endpoint: 'db/document-reference/',
    displayFields: ['name', 'title', 'id'],
  },
  journey_map: {
    value: 'journey_map',
    label: 'Journey Map',
    endpoint: 'db/journey-map/',
    displayFields: ['name', 'title', 'id'],
  },
  markdown: {
    value: 'markdown',
    label: 'Markdown Content',
    endpoint: '',
    displayFields: ['id'],
  },
  // A per-journey-map attachment. `content_ref_id` is a `journey_map_file.id`, resolved
  // through `db/journey-map-file/{id}/`, which is why the endpoint here is blank: the row is
  // deliberately absent from `model_map` so the generic lookup cannot reach it.
  //
  // Retained for steps authored before file resources existed. New steps use
  // `resource_file` below; nothing rewrites the existing ones, because these two carry
  // different id spaces and a blind repoint would silently resolve the wrong file.
  file: {
    value: 'file',
    label: 'File (map attachment)',
    endpoint: '',
    displayFields: ['id'],
  },
  // A file-backed `resource` row: uploaded once, referenceable from any number of steps.
  // `content_ref_id` is a *toolbox_db* `resource.id`, which is the whole reason this is a
  // separate type from `file` above — both would otherwise claim `content_ref_type='file'`
  // while meaning different tables, and a step would resolve to whichever row happened to
  // share the number.
  //
  // The endpoint is `db/resource/` so labels resolve through the generic lookup like any
  // other record; the bytes come from `db/resource-file/{id}/download-url/`.
  resource_file: {
    value: 'resource_file',
    label: 'File',
    endpoint: 'db/resource/',
    displayFields: ['file_name', 'name', 'id'],
  },
  // A self-hosted video, stored exactly like `resource_file` (a file-backed `resource` row,
  // referenced from `provider_config.file`), but rendered in a native <video> player that
  // tracks watched coverage for completion. Kept as its own type so the builder can offer a
  // completion-threshold field and the learner UI can pick the tracking player rather than the
  // generic file viewer. `content_ref_id`, when set, is a toolbox_db `resource.id`.
  video: {
    value: 'video',
    label: 'Video',
    endpoint: 'db/resource/',
    displayFields: ['file_name', 'name', 'id'],
  },
  link: {
    value: 'link',
    label: 'External Link',
    endpoint: '',
    displayFields: ['id'],
  },
  dcloud_catalog: {
    value: 'dcloud_catalog',
    label: 'dCloud Catalog Result',
    endpoint: '',
    displayFields: ['name', 'title', 'uid', 'contentId', 'id'],
  },
  // Series come from Snowflake, not a toolbox_db model, so there is no generic endpoint to
  // resolve a label from; the builder stores the series name on the item instead.
  mindtickle_series: {
    value: 'mindtickle_series',
    label: 'Mindtickle Series',
    endpoint: '',
    displayFields: ['series_name', 'name', 'series_id', 'id'],
  },
};

export const JOURNEY_MAP_PROVIDER_OPTIONS: JourneyMapProviderOption[] = [
  {
    value: 'cyber_academy',
    label: 'Cyber Academy',
    defaultContentRefType: 'ai_snackable_course',
    contentRefTypes: ['ai_snackable_course'],
  },
  {
    value: 'moodle',
    label: 'Moodle',
    defaultContentRefType: 'ai_snackable_course',
    contentRefTypes: ['ai_snackable_course'],
  },
  {
    value: 'journey_map',
    label: 'Journey Map',
    defaultContentRefType: 'journey_map',
    contentRefTypes: ['journey_map'],
  },
  {
    value: 'markdown',
    label: 'Markdown',
    defaultContentRefType: 'markdown',
    contentRefTypes: ['markdown'],
  },
  {
    value: 'file',
    label: 'File',
    // New steps get `resource_file`, so a dropped file is registered once and can be reused.
    // `file` stays selectable so an existing map attachment can still be edited in place
    // rather than being silently migrated to a different id space.
    defaultContentRefType: 'resource_file',
    contentRefTypes: ['resource_file', 'file'],
  },
  {
    value: 'video',
    label: 'Video',
    defaultContentRefType: 'video',
    contentRefTypes: ['video'],
  },
  {
    value: 'link',
    label: 'Link',
    defaultContentRefType: 'link',
    contentRefTypes: ['link'],
  },
  {
    value: 'toolbox_resource',
    label: 'Toolbox Resource',
    defaultContentRefType: 'resource',
    contentRefTypes: ['resource'],
  },
  {
    value: 'document',
    label: 'Document',
    defaultContentRefType: 'document_reference',
    contentRefTypes: ['document_reference'],
  },
  {
    value: 'dcloud',
    label: 'dCloud',
    defaultContentRefType: 'dcloud_catalog',
    contentRefTypes: ['dcloud_catalog'],
  },
  {
    value: 'mindtickle',
    label: 'Mindtickle',
    defaultContentRefType: 'mindtickle_series',
    contentRefTypes: ['mindtickle_series'],
  },
];

/**
 * Content ref types whose `content_ref_id` is *not* a toolbox_db primary key.
 *
 * Several consumers resolve a referenced record by convention, building
 * `db/{content_ref_type with - for _}/{content_ref_id}/`. That only holds when a toolbox_db
 * model actually backs the type. For the types below the id means something else — a Moodle
 * course id, a Mindtickle series id, inline markdown, a file row — so the request would
 * either 404 or, worse, return a same-numbered row from an unrelated table and be treated as
 * the referenced record.
 *
 * Deliberately a denylist rather than "does this option have an endpoint": an unrecognized
 * type keeps its previous behaviour instead of silently losing its content lookup.
 */
const NON_RECORD_CONTENT_REF_TYPES = new Set([
  'markdown',
  'file',
  'link',
  'dcloud_catalog',
  'mindtickle_series',
  'moodle_course',
]);

/** Whether `db/{type}/{id}/` is a meaningful lookup for this content ref type. */
export function hasToolboxDbRecord(contentRefType?: string | null): boolean {
  const normalized = String(contentRefType || '').trim().toLowerCase();
  if (!normalized) return false;
  return !NON_RECORD_CONTENT_REF_TYPES.has(normalized);
}

/** Both of the content ref types a Moodle / Cyber Academy course step can carry. */
export function isMoodleCourseContentRefType(contentRefType?: string | null): boolean {
  const normalized = String(contentRefType || '').trim().toLowerCase();
  return normalized === 'ai_snackable_course' || normalized === 'moodle_course';
}

/**
 * Both of the content ref types a file step can carry.
 *
 * Use this to decide "is this step a file at all". To fetch anything you must still know
 * *which* of the two it is, because the id spaces and endpoints differ — see
 * `isResourceFileContentRefType`.
 */
export function isFileContentRefType(contentRefType?: string | null): boolean {
  const normalized = String(contentRefType || '').trim().toLowerCase();
  return normalized === 'file' || normalized === 'resource_file';
}

/**
 * True for the reusable, `resource`-backed file type; false for a legacy map attachment.
 *
 * This is the branch that keeps the two id spaces apart: `resource_file` ids go to
 * `db/resource-file/{id}/`, plain `file` ids go to `db/journey-map-file/{id}/`. Sending an id
 * to the wrong one does not error — it returns a different file — so never infer the endpoint
 * from anything but the content ref type.
 */
export function isResourceFileContentRefType(contentRefType?: string | null): boolean {
  return String(contentRefType || '').trim().toLowerCase() === 'resource_file';
}

/**
 * True for the self-hosted video type. Backed by the same `resource` file row as
 * `resource_file` (bytes live in `provider_config.file`), but rendered by the tracking
 * <video> player rather than the generic file viewer.
 */
export function isVideoContentRefType(contentRefType?: string | null): boolean {
  return String(contentRefType || '').trim().toLowerCase() === 'video';
}

export function getProviderOption(value?: string | null) {
  if (!value) return null;
  return (
    JOURNEY_MAP_PROVIDER_OPTIONS.find(
      (p) => String(p.value).trim().toLowerCase() === String(value).trim().toLowerCase(),
    ) || null
  );
}

export function getContentRefTypeOption(value?: string | null) {
  if (!value) return null;
  return JOURNEY_MAP_CONTENT_REF_TYPES[value] || null;
}

export function getAllowedContentRefTypeOptions(
  provider?: string | null,
): JourneyMapContentRefTypeOption[] {
  const providerOption = getProviderOption(provider);
  if (!providerOption) return Object.values(JOURNEY_MAP_CONTENT_REF_TYPES);

  return providerOption.contentRefTypes
    .map((value) => JOURNEY_MAP_CONTENT_REF_TYPES[value])
    .filter(Boolean);
}

export function getDefaultContentRefTypeForProvider(provider?: string | null): string {
  return getProviderOption(provider)?.defaultContentRefType || '';
}

export function getContentOptionLabel(item: any, displayFields?: string[]): string {
  if (!item) return '';
  const fields =
    displayFields && displayFields.length > 0
      ? displayFields
      : ['name', 'title', 'id'];

  for (const field of fields) {
    if (
      item[field] !== undefined &&
      item[field] !== null &&
      String(item[field]).trim() !== ''
    ) {
      return String(item[field]);
    }
  }

  return String(item.id ?? '');
}